import { resolveSchema } from '@jsonforms/core';
import type { JsonSchema } from '@jsonforms/core';
import { get } from 'es-toolkit/compat';

/**
 * Extracts property options from a JSON schema for a given property path.
 *
 * This utility handles different schema patterns for defining options:
 * - Direct enum values
 * - anyOf/oneOf with const values
 * - $ref resolution
 *
 * @param schema The JSON schema to extract options from
 * @param propertyPath The dot-notation path to the property (e.g., 'properties.side')
 * @returns An array of option values, or null if no options are found
 *
 * @example
 * const schema = {
 *   properties: {
 *     side: {
 *       anyOf: [
 *         { const: 'buy' },
 *         { const: 'sell' }
 *       ]
 *     }
 *   }
 * };
 * const options = extractSchemaPropertyOptions(schema, 'properties.side');
 * // Returns: ['buy', 'sell']
 */
export function extractSchemaPropertyOptions<T = string>(schema: JsonSchema, propertyPath: string): T[] | null {
    const property: unknown = get(schema, propertyPath, null);

    if (!property || typeof property !== 'object') {
        return null;
    }

    const prop = property as Record<string, unknown>;

    const targetProp: JsonSchema = prop.$ref ? resolveSchema(prop, '', schema) : prop;

    const options = get(targetProp, 'anyOf', null) ?? get(targetProp, 'oneOf', null) ?? get(targetProp, 'enum', null);

    if (!options || !Array.isArray(options)) {
        return null;
    }

    // Extract values. Handle both { const: value } objects and enum strings
    return (options as Array<{ const: T } | T>).map((item) =>
        typeof item === 'object' && item !== null && 'const' in item ? item.const : (item as T),
    );
}
